import { TDeleteCompanyResDto } from "@/api/services/admin/company/dtos/delete-company.dto";
import { TCreateRegisterSupportOrganizationReqDto, TCreateRegisterSupportOrganizationResDto } from "@/api/services/admin/rso/dtos/create-rso.dto";
import {
  TGetRegisterSupportOrganizationCountReqDto,
  TGetRegisterSupportOrganizationCountResDto,
} from "@/api/services/admin/rso/dtos/get-rso-count.dto";
import { TGetRegisterSupportOrganizationListResDto } from "@/api/services/admin/rso/dtos/get-rso-list.dto";
import { TGetRegisterSupportOrganizationResDto } from "@/api/services/admin/rso/dtos/get-rso.dto";
import { TSwitchUserAsAdminResDto } from "@/api/services/admin/rso/dtos/switch-user-as-admin.dto";
import { TUpdateRegisterSupportOrganizationReqDto, TUpdateRegisterSupportOrganizationResDto } from "@/api/services/admin/rso/dtos/update-rso.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TApiRsoListPagReq } from "@/types/api";
import { clientApi } from "@/utils/api";
import { toNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

const SERVICE_BASE_PATH = "/register-support-organizations";

export const createRegisterSupportOrganizationApi = async (
  payload: TCreateRegisterSupportOrganizationReqDto,
): Promise<TCreateRegisterSupportOrganizationResDto> => {
  const rs = await clientApi.post<TCreateRegisterSupportOrganizationResDto>("/register-support-organizations", payload);
  return rs.data;
};

export const fetchRegisterSupportOrganizationApi = async (rsoId: string): Promise<TGetRegisterSupportOrganizationResDto> => {
  const rs = await clientApi.get<TGetRegisterSupportOrganizationResDto>(`/register-support-organizations/${rsoId}`);
  return rs.data;
};

export const useRegisterSupportOrganization = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchRegisterSupportOrganizationApi(id) : undefined),
    enabled: Boolean(id),
  });
};

export const fetchRegisterSupportOrganizationListApi = async (req: TApiRsoListPagReq): Promise<TGetRegisterSupportOrganizationListResDto> => {
  const url = createUrlWithParams("/register-support-organizations", req);

  const rs = await clientApi.get<TGetRegisterSupportOrganizationListResDto>(url);
  return rs.data;
};

export const useRegisterSupportOrganizationList = (req: TApiRsoListPagReq) => {
  const params = {
    ...req,
    sorts: toNumber(req.sorts?.length) > 0 ? req.sorts : ["rso.createdAt|DESC"],
  };
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_LIST, params],
    queryFn: () => fetchRegisterSupportOrganizationListApi(params),
  });
};

export const setRsoAdminApi = async (rsoId: string, userId: number): Promise<TSwitchUserAsAdminResDto> => {
  const rs = await clientApi.put<TSwitchUserAsAdminResDto>(`/register-support-organizations/${rsoId}/users/${userId}/set-as-admin`);
  return rs.data;
};

export const updateRegisterSupportOrganizationApi = async (
  rsoId: string,
  payload: TUpdateRegisterSupportOrganizationReqDto,
): Promise<TUpdateRegisterSupportOrganizationResDto> => {
  const rs = await clientApi.put<TUpdateRegisterSupportOrganizationResDto>(`/register-support-organizations/${rsoId}`, payload);
  return rs.data;
};

export const fetchRegisterSupportOrganizationCountApi = async (
  req?: TGetRegisterSupportOrganizationCountReqDto,
): Promise<TGetRegisterSupportOrganizationCountResDto> => {
  const url = createUrlWithParams("/register-support-organizations/count", req);
  const rs = await clientApi.get<TGetRegisterSupportOrganizationCountResDto>(url);
  return rs.data;
};

export const useRegisterSupportOrganizationCount = (req?: TGetRegisterSupportOrganizationCountReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_COUNT, req],
    queryFn: () => fetchRegisterSupportOrganizationCountApi(req),
  });
};

export const deleteRegisterSupportOrganizaion = async (rsoId: string): Promise<TDeleteCompanyResDto> => {
  const response = await clientApi.delete<TDeleteCompanyResDto>(`${SERVICE_BASE_PATH}/${rsoId}`);
  return response.data;
};
